import { CheckCircle2, Download, Printer, Send, ShieldCheck, UploadCloud } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SaveAndResumeLater, type SaveDraftResult } from "../../components/forms/SaveAndResumeLater";
import { buildSubmissionSummary, type SubmissionSummary } from "../../lib/formSubmissionSummary";
import {
  appendStoredDraftFiles,
  buildResumeUrl,
  getCurrentSectionId,
  getResumeDraftId,
  loadFormProgress,
  restoreFormProgress,
  saveFormProgress,
  type DraftFileMeta,
} from "../../lib/formProgress";
import { FormPortalHeader } from "./FormPortalHeader";
import { rsvpSections, rsvpSummarySections } from "./formsContent";

type FormErrors = Record<string, string>;

const RSVP_FORM_KEY = "rsvp-hub";
const RSVP_FORM_PATH = "/rsvp-hub";
const RSVP_FORM_TITLE = "RSVP Hub Form";

const requiredTextFields = [
  "fullName",
  "email",
  "phone",
  "preferredContactMethod",
  "eventType",
  "eventDate",
  "eventName",
] as const;

const requiredCheckboxFields = ["accurateConfirmation", "contentPermission", "serviceUseConfirmation", "changeRequestAcknowledgement"] as const;

const requiredLabels: Record<string, string> = {
  fullName: "Full Name is required.",
  email: "Email Address is required.",
  phone: "Phone Number is required.",
  preferredContactMethod: "Preferred Contact Method is required.",
  eventType: "Event Type is required.",
  eventDate: "Event Date is required.",
  eventName: "Event Name or Honoree Name is required.",
  accurateConfirmation: "Please confirm that the information provided is accurate.",
  contentPermission: "Please confirm that you have permission to submit the content and files provided.",
  serviceUseConfirmation: "Please confirm that Kreatethedate may use these details to create your requested service.",
  changeRequestAcknowledgement: "Please confirm that you understand major setup changes may require additional time and/or fees.",
};

const eventTypeOptions = [
  "Wedding",
  "Birthday",
  "Graduation",
  "Baby Shower",
  "Anniversary",
  "Memorial",
  "Fundraiser",
  "Corporate Event",
  "Community Event",
  "Other",
];

const preferenceOptions = ["Yes", "No", "Not sure yet"];

const futureServicesOptions = [
  "Memory Hub",
  "Archive Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
  "Not at this time",
];

export function RSVPHubFormPage() {
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState<SubmissionSummary | null>(null);
  const [draftId, setDraftId] = useState("");
  const [restoreNotice, setRestoreNotice] = useState("");
  const [restoredFiles, setRestoredFiles] = useState<DraftFileMeta[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    document.title = "RSVP Hub Form | Kreatethedate";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Complete the Kreatethedate RSVP Hub Form with event details, RSVP preferences, design notes, and confirmations.");
  }, []);

  useEffect(() => {
    const resumeDraftId = getResumeDraftId();
    if (!resumeDraftId) {
      const targetSectionId = window.location.hash.replace("#", "");
      if (targetSectionId) {
        window.setTimeout(() => {
          document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
        }, 80);
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
      return;
    }

    const draft = loadFormProgress(RSVP_FORM_KEY, resumeDraftId);
    if (!draft || !formRef.current) {
      setRestoreNotice("We could not find saved progress for this resume link in this browser.");
      return;
    }

    restoreFormProgress(formRef.current, draft);
    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    setRestoreNotice("Your saved progress has been restored in this browser.");

    const targetSectionId = window.location.hash.replace("#", "") || draft.lastSectionId || rsvpSections[0]?.id;
    window.setTimeout(() => {
      document.getElementById(targetSectionId)?.scrollIntoView({ block: "start" });
    }, 80);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set("formType", "rsvp-hub");
    formData.set("cf-turnstile-response", "turnstile-placeholder");
    if (draftId) await appendStoredDraftFiles(formData, RSVP_FORM_KEY, draftId);

    const nextErrors = validateRsvpForm(formData);
    setErrors(nextErrors);

    const firstInvalidName = Object.keys(nextErrors)[0];
    if (firstInvalidName) {
      form.querySelector<HTMLElement>(`[name="${firstInvalidName}"]`)?.focus();
      return;
    }

    const summary = buildSubmissionSummary({
      formData,
      formTitle: "RSVP Hub Setup Form",
      clientNameField: "fullName",
      eventNameField: "eventName",
      eventDateField: "eventDate",
      sections: rsvpSummarySections,
    });

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/forms/rsvp-hub", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const message = await response.text();
        if (!(import.meta.env.DEV && response.status === 404)) {
          throw new Error(message || "Your form could not be submitted. Please try again.");
        }
      }

      const contentType = response.headers.get("Content-Type") || "";
      if (response.ok && !contentType.includes("application/json") && !import.meta.env.DEV) {
        throw new Error("The RSVP Hub submission endpoint is not configured yet.");
      }

      setSubmittedSummary(summary);
      setSubmitted(true);
      formRef.current?.reset();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Your form could not be submitted. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handlePrintCompletedForm() {
    printSubmittedForm(submittedSummary);
  }

  function handleDownloadCompletedForm() {
    printSubmittedForm(submittedSummary);
  }

  async function handleSaveProgress(): Promise<SaveDraftResult> {
    if (!formRef.current) throw new Error("The form is not ready yet.");

    const lastSectionId = getCurrentSectionId(rsvpSections.map((section) => section.id));
    const draft = await saveFormProgress({
      form: formRef.current,
      formKey: RSVP_FORM_KEY,
      draftId,
      lastSectionId,
    });
    const resumeLink = buildResumeUrl(RSVP_FORM_PATH, draft.draftId, lastSectionId);

    setDraftId(draft.draftId);
    setRestoredFiles(draft.fileMeta);
    return { draft, resumeLink };
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <a
        href="#rsvp-form"
        className="focus-ring sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#050505] focus:px-4 focus:py-3 focus:text-[#FF6A2B]"
      >
        Skip to RSVP Hub form
      </a>
      <FormPortalHeader />

      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18">
        <img src="/assets/celebration-table.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,5,5,.94),rgba(5,5,5,.74)_54%,rgba(5,5,5,.48)),linear-gradient(0deg,rgba(5,5,5,.42),rgba(5,5,5,.14))]" />
        <div className="relative mx-auto grid min-h-[46dvh] max-w-7xl items-end gap-8 px-4 pb-10 pt-24 text-[#fff9ef] sm:px-6 lg:grid-cols-[1fr_.72fr] lg:px-8 lg:pb-14">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6A2B]">RSVP Hub Form</p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              Let&apos;s Build Your RSVP Hub
            </h1>
          </div>
          <p className="max-w-xl border-l-2 border-[#FF6A2B] pl-4 text-sm font-semibold leading-7 text-[#fff9ef]/76 lg:ml-auto">
            Share your event details, preferences, photos, and ideas so we can create a beautiful RSVP experience for your guests.
          </p>
        </div>
      </section>

      {submitted ? (
        <ThankYouState
          onDownloadPdf={handleDownloadCompletedForm}
          onPrint={handlePrintCompletedForm}
          summary={submittedSummary}
        />
      ) : (
        <section className="px-4 pb-28 pt-10 sm:px-6 lg:px-8 lg:pb-28 lg:pt-14">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[290px_1fr]">
            <aside className="hidden h-fit rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] lg:sticky lg:top-24 lg:block">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#D94A16]">Form sections</p>
              <ol className="mt-4 grid gap-2">
                {rsvpSections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="focus-ring group grid grid-cols-[34px_1fr] items-center gap-3 rounded-lg px-2 py-2 text-sm font-bold text-[#5c5246] transition hover:bg-[#fff9ef] hover:text-[#050505]"
                    >
                      <span className="grid size-8 place-items-center rounded-full border border-[#FF6A2B]/45 bg-[#050505] text-xs text-[#FF6A2B]">
                        {index + 1}
                      </span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <form id="rsvp-form" ref={formRef} noValidate onSubmit={handleSubmit} className="grid gap-6">
              <SaveAndResumeLater
                formTitle={RSVP_FORM_TITLE}
                getDefaultEmail={() => {
                  const emailControl = formRef.current?.elements.namedItem("email");
                  return emailControl instanceof HTMLInputElement ? emailControl.value : "";
                }}
                onSaveDraft={handleSaveProgress}
                restoreNotice={restoreNotice}
                savedFiles={restoredFiles}
              />
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Estimated completion time: 10&ndash;15 minutes. You can save your progress and return at any time.
              </p>
              <p className="rounded-lg border border-[#D94A16]/20 bg-white px-5 py-4 text-sm font-semibold leading-7 text-[#5c5246]">
                Required fields are marked with <span className="font-black text-[#D94A16]">required</span>.
              </p>

              <FormSection id="contact" number="01" title="Client Contact Information">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="fullName" label="Full Name" autoComplete="name" required error={errors.fullName} />
                  <TextField name="email" label="Email Address" type="email" autoComplete="email" required error={errors.email} />
                  <TextField name="phone" label="Phone Number" type="tel" autoComplete="tel" required error={errors.phone} />
                  <SelectField
                    name="preferredContactMethod"
                    label="Preferred Contact Method"
                    required
                    error={errors.preferredContactMethod}
                    options={["Email", "Phone", "Text"]}
                  />
                </div>
              </FormSection>

              <FormSection id="additional-contact" number="02" title="Additional Event Contact (Optional)">
                <div className="grid gap-4">
                  <p className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    Provide an additional contact person if someone else is helping coordinate the event.
                  </p>
                  <div className="grid gap-4 md:grid-cols-3">
                    <TextField name="additionalContactName" label="Name" autoComplete="name" />
                    <TextField name="additionalContactPhone" label="Phone Number" type="tel" autoComplete="tel" />
                    <TextField name="additionalContactEmail" label="Email Address" type="email" autoComplete="email" />
                  </div>
                </div>
              </FormSection>

              <FormSection id="event-details" number="03" title="Event Details">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField name="eventType" label="Event Type" required error={errors.eventType} options={eventTypeOptions} />
                  <TextField name="eventName" label="Event Name or Honoree Name" required error={errors.eventName} />
                  <TextField name="eventDate" label="Event Date" type="date" required error={errors.eventDate} />
                  <TextField name="eventStartTime" label="Event Start Time" type="time" />
                  <TextField name="eventEndTime" label="Event End Time" type="time" />
                  <TextField name="eventLocationName" label="Event Location Name" />
                  <TextField name="eventAddress" label="Event Address" className="md:col-span-2" />
                  <SelectField name="eventVisibility" label="Is this event public or private?" options={["Private", "Public", "Not sure yet"]} />
                  <TextareaField name="eventNotes" label="Any special notes about the event?" className="md:col-span-2" rows={4} />
                </div>
              </FormSection>

              <FormSection id="rsvp-page" number="04" title="RSVP Page Details">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="rsvpTitle" label="What title should appear on the RSVP Hub?" className="md:col-span-2" />
                  <TextareaField name="welcomeMessage" label="Short welcome message or event description" className="md:col-span-2" rows={4} />
                  <TextField name="dressCode" label="Dress code" />
                  <TextField name="registryLink" label="Registry link" type="url" />
                  <TextField name="giftLink" label="Gift/donation link" type="url" />
                  <TextField name="menuDetails" label="Menu or catering details" />
                  <TextareaField name="parkingInstructions" label="Parking instructions" rows={3} />
                  <TextareaField name="hotelTravelInfo" label="Hotel/travel information if applicable" rows={3} />
                </div>
              </FormSection>

              <FormSection id="guest-preferences" number="05" title="Guest / RSVP Preferences">
                <div className="grid gap-4 md:grid-cols-2">
                  <SelectField name="rsvpYesNo" label="Do guests need to RSVP yes or no?" options={preferenceOptions} />
                  <SelectField name="allowAdditionalGuests" label="Should guests be able to add additional guests?" options={preferenceOptions} />
                  <SelectField name="mealOptions" label="Should guests select meal options?" options={preferenceOptions} />
                  <SelectField name="customQuestions" label="Should guests answer custom questions?" options={preferenceOptions} />
                  <TextField name="rsvpDeadline" label="RSVP deadline date" type="date" />
                  <TextField name="eventQuestionContact" label="Contact person for event questions" />
                  <TextareaField name="customQuestionDetails" label="Custom questions or guest notes" className="md:col-span-2" rows={4} />
                </div>
              </FormSection>

              <FormSection id="design" number="06" title="Design Preferences">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="preferredColors" label="Preferred colors" />
                  <TextField name="eventTheme" label="Event theme/style" />
                  <FileField name="logoFile" label="Upload logo if applicable" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" />
                  <FileField name="inspirationImages" label="Upload inspiration images if applicable" accept="image/*,.pdf" multiple />
                  <TextareaField
                    name="designAvoid"
                    label="Any design preferences or things to avoid?"
                    className="md:col-span-2"
                    rows={4}
                  />
                </div>
              </FormSection>

              <FormSection id="uploads" number="07" title="Uploads / Inspiration Files">
                <div className="grid gap-4">
                  <p className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    Add inspiration images, logos, flyers, or wording files here. Large files may be requested separately
                    after Kreatethedate reviews your form.
                  </p>
                  <FileField
                    name="rsvpHubPhotos"
                    label="RSVP Hub Photos / Featured Images"
                    accept=".jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp"
                    multiple
                    helperText="Upload any photos you would like Kreatethedate to consider using on your RSVP Hub. These may include honoree photos, couple photos, graduation photos, family photos, maternity photos, memorial photos, or other featured images related to the event."
                  />
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>Recommended: up to 10 images for now.</p>
                    <p>This upload area is for photos used to help design the RSVP Hub before the event, not for guest memory uploads after the event.</p>
                    <p>For full event photo and video collection after the event, please use the Memory Hub or Memory Media Vault form.</p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <FileField name="flyerFiles" label="Flyers or event graphics" accept="image/*,.pdf" multiple />
                    <FileField name="wordingFiles" label="Wording files or documents" accept=".pdf,.doc,.docx,.txt,.rtf,image/*" multiple />
                  </div>
                </div>
              </FormSection>

              <FormSection id="deadline" number="08" title="Deadline & Special Instructions">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField name="desiredCompletionDate" label="Desired completion date" type="date" />
                  <SelectField name="rushRequest" label="Is this a rush request?" options={["No", "Yes", "Not sure yet"]} />
                  <TextareaField name="additionalNotes" label="Anything else Kreatethedate should know?" className="md:col-span-2" rows={5} />
                </div>
              </FormSection>

              <FormSection id="creative-ideas" number="09" title="Special Instructions & Additional Information">
                <TextareaField
                  name="creativeIdeas"
                  label="Any Creative Ideas You Would Like Kreatethedate to Consider?"
                  helperText="Help us bring your vision to life. Share any special ideas, themes, interactive elements, surprise features, memory concepts, family traditions, tribute ideas, design inspirations, or unique touches you would like us to consider while creating your experience."
                  rows={8}
                />
              </FormSection>

              <FormSection id="future-services" number="10" title="Future Services Interest">
                <CheckboxGroupField
                  name="futureServicesInterest"
                  label="Would you like information about additional Kreatethedate services?"
                  options={futureServicesOptions}
                />
              </FormSection>

              <FormSection id="confirmation" number="11" title="Permission & Confirmation">
                <div className="grid gap-4">
                  <CheckboxField
                    name="accurateConfirmation"
                    label="I confirm that the information provided is accurate."
                    required
                    error={errors.accurateConfirmation}
                  />
                  <CheckboxField
                    name="contentPermission"
                    label="I confirm that I have permission to submit any photos, videos, logos, wording, or files provided for this project."
                    required
                    error={errors.contentPermission}
                  />
                  <CheckboxField
                    name="serviceUseConfirmation"
                    label="I understand Kreatethedate will use the submitted information to create my requested service."
                    required
                    error={errors.serviceUseConfirmation}
                  />
                  <TurnstilePlaceholder />
                </div>
              </FormSection>

              {submitError ? (
                <p className="rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-sm font-bold text-red-800" role="alert">
                  {submitError}
                </p>
              ) : null}

              <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
                <div className="grid gap-4">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">RSVP Hub Form</p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#050505]">Change Request Notice</h2>
                  </div>
                  <div className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
                    <p>Please review your information carefully before submitting.</p>
                    <p>
                      Kreatethedate will use the information provided to create your RSVP Hub. Major changes requested
                      after setup has begun may affect the project timeline and may require additional work, revisions, or
                      fees.
                    </p>
                    <p>
                      Submitting this form helps ensure your project can be completed as efficiently and accurately as
                      possible.
                    </p>
                  </div>
                  <CheckboxField
                    name="changeRequestAcknowledgement"
                    label="I understand that major changes requested after the RSVP Hub setup has started may require additional time and/or additional fees."
                    required
                    error={errors.changeRequestAcknowledgement}
                  />
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-semibold leading-7 text-[#5c5246]">
                  Please review your details before submitting your RSVP Hub form.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-6 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a] disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit RSVP Hub Form"}
                  <Send className="size-4" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function validateRsvpForm(formData: FormData) {
  const nextErrors: FormErrors = {};

  for (const name of requiredTextFields) {
    const value = String(formData.get(name) || "").trim();
    if (!value) nextErrors[name] = requiredLabels[name];
  }

  const email = String(formData.get("email") || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    nextErrors.email = "Enter a valid email address.";
  }

  for (const name of requiredCheckboxFields) {
    if (!formData.get(name)) nextErrors[name] = requiredLabels[name];
  }

  return nextErrors;
}

function ThankYouState({
  onDownloadPdf,
  onPrint,
  summary,
}: {
  onDownloadPdf: () => void;
  onPrint: () => void;
  summary: SubmissionSummary | null;
}) {
  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-5xl gap-6">
        <div className="print-hide rounded-lg border border-[#D94A16]/24 bg-white p-6 text-center shadow-[0_24px_70px_rgba(45,32,8,.12)] sm:p-8">
          <span className="mx-auto grid size-16 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
            <CheckCircle2 className="size-9" aria-hidden="true" />
          </span>
          <h2 className="mt-6 font-display text-4xl font-bold text-[#050505]">Thank you.</h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg leading-8 text-[#5c5246]">
            Thank you. Your Kreatethedate RSVP Hub form has been received. We will review your details and follow up with
            next steps.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onPrint}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
            >
              <Printer className="size-4" aria-hidden="true" />
              Print My Form
            </button>
            <button
              type="button"
              onClick={onDownloadPdf}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/35 bg-[#fff9ef] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-white"
            >
              <Download className="size-4" aria-hidden="true" />
              Download My Form as PDF
            </button>
            <Link
              to="/"
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg border border-[#D94A16]/30 px-5 text-sm font-extrabold text-[#5c5246] transition hover:bg-[#fff9ef]"
            >
              Back to Forms Portal
            </Link>
          </div>
        </div>

        {summary ? <SubmittedFormSummary summary={summary} /> : null}
      </div>
    </section>
  );
}

function SubmittedFormSummary({ summary }: { summary: SubmissionSummary }) {
  return (
    <article className="print-document rounded-lg border border-[#D94A16]/24 bg-white p-6 shadow-[0_24px_70px_rgba(45,32,8,.1)] sm:p-8">
      <header className="border-b border-[#D94A16]/22 pb-5">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#D94A16]">{summary.businessName}</p>
        <h2 className="mt-2 font-display text-4xl font-bold text-[#050505]">{summary.formTitle}</h2>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <SummaryMeta label="Client name" value={summary.clientName} />
          <SummaryMeta label="Event name" value={summary.eventName} />
          <SummaryMeta label="Event date" value={summary.eventDate} />
          <SummaryMeta label="Date submitted" value={summary.submittedAt} />
        </dl>
      </header>

      <div className="mt-6 grid gap-6">
        {summary.sections.map((section) => (
          <section key={section.title} className="break-inside-avoid rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
            <h3 className="font-display text-2xl font-bold text-[#050505]">{section.title}</h3>
            <dl className="mt-4 grid gap-3">
              {section.answers.map((answer) => (
                <div key={`${section.title}-${answer.label}`} className="grid gap-1 border-t border-[#D94A16]/14 pt-3">
                  <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{answer.label}</dt>
                  <dd className="whitespace-pre-wrap text-sm font-semibold leading-7 text-[#332a1f]">{answer.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </article>
  );
}

function SummaryMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3">
      <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</dt>
      <dd className="mt-1 font-bold leading-6 text-[#16120a]">{value}</dd>
    </div>
  );
}

function printSubmittedForm(summary: SubmissionSummary | null) {
  const previousTitle = document.title;
  if (summary) document.title = `${summary.formTitle} - ${summary.clientName}`;
  window.print();
  window.setTimeout(() => {
    document.title = previousTitle;
  }, 500);
}

function FormSection({ id, number, title, children }: { id: string; number: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-28 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
      <div className="mb-5 flex items-start gap-4 border-b border-[#D94A16]/16 pb-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[#050505] text-sm font-black text-[#FF6A2B]">
          {number}
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">RSVP Hub Form</p>
          <h2 id={`${id}-heading`} className="mt-1 font-display text-2xl font-bold text-[#050505]">
            {title}
          </h2>
        </div>
      </div>
      {children}
    </section>
  );
}

interface BaseFieldProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  helperText?: string;
}

interface TextFieldProps extends BaseFieldProps {
  type?: string;
  autoComplete?: string;
}

function TextField({ name, label, required, error, className = "", type = "text", autoComplete }: TextFieldProps) {
  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

function TextareaField({ name, label, required, error, className = "", helperText, rows = 3 }: BaseFieldProps & { rows?: number }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <textarea
        id={name}
        name={name}
        rows={rows}
        required={required}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={`form-field ${fieldSpacing} w-full rounded-lg px-4 py-3 text-base leading-7 focus:outline-none`}
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

function SelectField({ name, label, required, error, className = "", options }: BaseFieldProps & { options: string[] }) {
  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      <select
        id={name}
        name={name}
        required={required}
        defaultValue=""
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      >
        <option value="" disabled>
          Select one
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <FieldError name={name} error={error} />
    </div>
  );
}

function FileField({ name, label, className = "", accept, multiple, helperText }: BaseFieldProps & { accept?: string; multiple?: boolean }) {
  const describedBy = helperText ? `${name}-helper` : undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <label className={`${fieldSpacing} flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[#D94A16]/36 bg-[#fff9ef] px-4 py-5 text-center text-sm font-bold text-[#5c5246] transition hover:border-[#D94A16]/60 hover:bg-white`}>
        <UploadCloud className="size-6 text-[#D94A16]" aria-hidden="true" />
        Choose file{multiple ? "s" : ""}
        <input id={name} name={name} type="file" accept={accept} multiple={multiple} aria-describedby={describedBy} className="sr-only" />
      </label>
    </div>
  );
}

function CheckboxGroupField({ name, label, options, className = "" }: BaseFieldProps & { options: string[] }) {
  return (
    <fieldset className={className}>
      <legend className="text-sm font-black text-[#16120a]">{label}</legend>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option}
            className="flex min-h-12 items-start gap-3 rounded-lg border border-[#D94A16]/22 bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538]"
          >
            <input name={name} type="checkbox" value={option} className="mt-1 size-5 shrink-0 accent-[#050505]" />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CheckboxField({ name, label, required, error }: BaseFieldProps) {
  return (
    <div>
      <label
        className={`flex gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
          error ? "border-red-400" : "border-[#D94A16]/22"
        }`}
      >
        <input
          id={name}
          name={name}
          type="checkbox"
          required={required}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `${name}-error` : undefined}
          className="mt-1 size-5 shrink-0 accent-[#050505]"
        />
        <span>
          {label} {required ? <span className="font-black text-[#D94A16]">required</span> : null}
        </span>
      </label>
      <FieldError name={name} error={error} />
    </div>
  );
}

function TurnstilePlaceholder() {
  return (
    <div className="rounded-lg border border-[#D94A16]/22 bg-[#fff9ef] p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
          <ShieldCheck className="size-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-black text-[#050505]">Security verification</p>
          <p className="mt-1 text-sm font-semibold leading-6 text-[#5c5246]">
            A short verification step will appear here before public launch.
          </p>
        </div>
      </div>
      <input type="hidden" name="cf-turnstile-response" value="turnstile-placeholder" />
    </div>
  );
}

function FieldLabel({ name, label, required }: { name: string; label: string; required?: boolean }) {
  return (
    <label htmlFor={name} className="text-sm font-black text-[#16120a]">
      {label} {required ? <span className="text-xs uppercase tracking-[0.14em] text-[#D94A16]">required</span> : null}
    </label>
  );
}

function FieldError({ name, error }: { name: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={`${name}-error`} className="mt-2 text-sm font-bold text-red-700" role="alert">
      {error}
    </p>
  );
}
